import * as React from 'react';

import { BLOCK_CODE_DEFAULT_OVERFLOW, type BlockCodeData, code } from '@gitbook/doc-core';
import { resolveCodeBlockSyntax, usePaletteHandle } from '@gitbook/react-uikit';
import type { CodeBlockSyntaxInfo } from '@gitbook/react-uikit/src/CodeBlocks/syntax-highlighting';
import { BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT,
    CodeBlock,
    CodeBlockBody,
    CodeBlockEditableTitle,
    CodeBlockExpandableFooter,
    CodeBlockHeader,
    CodeBlockToolbar,
    clipboard,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { BlockWrapper } from '../common';
import { createCodeSyntaxesPalette, useCodeBlockPaletteFactory } from './palette';

/**
 * Component to render a code block.
 */
export function BlockCode(props: BlockProps): React.ReactElement {
    const { editor, node, children } = props;
    const { readOnly } = editor;
    const data = node.data.toFrozenObject<BlockCodeData>();

    const syntax = resolveCodeBlockSyntax(data.syntax || 'text');
    const hasTitle = typeof data.title === 'string';

    const syntaxButtonRef = React.useRef<HTMLButtonElement>(null);
    const syntaxPaletteHandle = usePaletteHandle(syntaxButtonRef);

    const moreButtonRef = React.useRef<HTMLButtonElement>(null);
    const morePaletteHandle = usePaletteHandle(moreButtonRef);

    // Handler to rename a code block's title
    const onRename = useEventCallback((title: string) => {
        editor.change((change) => {
            change.insertDataByKey(node.key, ['title'], title);
        });
    });

    // Handler to select the block on `Escape` key press when cursor is in the caption.
    // This is needed because the caption is an input so it captures the `Escape` key press event.
    const onCaptionEscPress = useEventCallback(() => {
        if (!isBlockRangeSelection(editor.value.selection)) {
            const block = editor.value.document.assertBlock(node.key);
            const selection = BlockRange.create({
                anchorBlock: block.key,
                focusBlock: block.key,
                isFocused: true,
            });
            editor.change((change) => {
                change.selectBlock(selection);
            });
        }
    });

    /** Get the inner content of the code block to copy  */
    const contentToCopy = () => code.getCodeText(node);
    const palette = useCodeBlockPaletteFactory({ nodeKey: node.key });

    const numberOfLinesOfCode = node.nodes.size;
    const collapsedLineCount = data.collapsedLineCount || CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT;
    const expandable = Boolean(data.expandable && numberOfLinesOfCode > collapsedLineCount);

    const [isExpanded, setIsExpanded] = React.useState(false);

    return (
        <BlockWrapper {...props} palette={palette}>
            <CodeBlock
                showLineNumbers={Boolean(data.lineNumbers)}
                numberOfLines={numberOfLinesOfCode}
                editable={!readOnly}
                overflow={data.overflow || BLOCK_CODE_DEFAULT_OVERFLOW}
                expandable={expandable}
                isExpanded={isExpanded}
                collapsedLineCount={collapsedLineCount}
            >
                {hasTitle ? (
                    <CodeBlockHeader>
                        {readOnly ? (
                            data.title
                        ) : (
                            <CodeBlockEditableTitle
                                title={data.title}
                                onChange={onRename}
                                onEscape={onCaptionEscPress}
                            />
                        )}
                    </CodeBlockHeader>
                ) : null}
                <CodeBlockBody>{children}</CodeBlockBody>
                <CodeBlockToolbar show={syntaxPaletteHandle.opened || morePaletteHandle.opened}>
                    {readOnly ? null : (
                        <SyntaxSelector
                            node={node}
                            ref={syntaxButtonRef}
                            active={syntaxPaletteHandle.opened}
                            currentSyntax={syntax}
                            onSelect={() => {
                                syntaxPaletteHandle.open(
                                    createCodeSyntaxesPalette(editor, node.key)
                                );
                            }}
                        />
                    )}
                    <Button
                        label="Copy"
                        leadingIcon={Icon.Copy}
                        onPress={() => {
                            clipboard.writeText(contentToCopy());
                        }}
                    />
                    {readOnly ? null : (
                        <Button
                            ref={moreButtonRef}
                            leadingIcon={Icon.MoreVertical}
                            label="More options"
                            onPress={() => {
                                morePaletteHandle.open(palette());
                            }}
                        />
                    )}
                </CodeBlockToolbar>
                {expandable ? <CodeBlockExpandableFooter onChange={setIsExpanded} /> : null}
            </CodeBlock>
        </BlockWrapper>
    );
}

const SyntaxSelector = React.forwardRef<
    HTMLButtonElement,
    {
        active: boolean;
        onSelect: () => void;
        node: BlockProps['node'];
        currentSyntax: CodeBlockSyntaxInfo;
    }
>(function SyntaxSelector(props, ref) {
    const { currentSyntax, active, onSelect, ...rest } = props;

    return (
        <Button
            {...rest}
            iconOnly={false}
            ref={ref}
            active={active}
            trailingIcon={Icon.ChevronDown}
            onPress={onSelect}
            label={currentSyntax.title}
        />
    );
});
